import React from "react";

import { Button, Result } from "antd";
import * as Sentry from "@sentry/browser";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Custom fallback. Receives the caught error and a reset callback that
   * clears the error state so the children can re-render (e.g. after the
   * user retries). When omitted, a default antd <Result> is shown.
   */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /**
   * Extra label for Sentry so we can tell which boundary fired
   * (e.g. "data-modal", "app-root").
   */
  boundaryName?: string;
  /**
   * When any value in this array changes (shallow compare) while the boundary
   * is in the error state, the error is cleared and the children re-render.
   * Use it to auto-recover on context changes the error depended on — e.g.
   * pass the router location so navigating away from a broken route resets a
   * top-level boundary that would otherwise stay stuck until a hard reload.
   */
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Generic React error boundary. The app has historically had none, so any
 * uncaught render exception unmounted the whole tree and showed a blank white
 * page. This contains the blast radius to the wrapped subtree and reports the
 * error to Sentry.
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.withScope((scope) => {
      if (this.props.boundaryName) {
        scope.setTag("error_boundary", this.props.boundaryName);
      }
      scope.setExtra("componentStack", info.componentStack);
      Sentry.captureException(error);
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error === null) {
      return;
    }
    const prev = prevProps.resetKeys;
    const next = this.props.resetKeys;
    if (!prev || !next) {
      return;
    }
    const changed =
      prev.length !== next.length || next.some((key, i) => key !== prev[i]);
    if (changed) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <Result
        status="error"
        title="Something went wrong"
        subTitle="This section failed to render. The error has been reported. You can retry or reload the page."
        extra={[
          <Button key="retry" type="primary" onClick={this.reset}>
            Retry
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>
            Reload page
          </Button>,
        ]}
      />
    );
  }
}

export default ErrorBoundary;
